/**
 * Shrink a photo in the browser before it is uploaded.
 *
 * nginx and the backend both cap an upload at 10MB, and photos straight off a
 * phone or out of Lightroom are routinely 13-20MB, so they were rejected with
 * a 413 before reaching the app. A gallery never needs the full original, and
 * every byte sent is also stored in Postgres.
 *
 * Anything the browser cannot decode (HEIC in Chrome, say) is sent as-is and
 * left for the server to accept or explain.
 */
const SHRINK_ABOVE_BYTES = 5 * 1024 * 1024;
const MAX_EDGE_PX = 2560;
const JPEG_QUALITY = 0.85;

export async function shrinkImageForUpload(file) {
  if (file.size <= SHRINK_ABOVE_BYTES) return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  const scale = Math.min(1, MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
  );
  if (!blob) return file;

  const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
  return new File([blob], name, { type: 'image/jpeg' });
}
